<template>
	<div class="fixed top-16 mt-4 right-4 bg-black bg-opacity-95 text-white p-4 rounded-lg shadow-lg z-50 max-w-md">
		<div class="flex justify-between items-center mb-4">
			<h3 class="font-bold">
				GA4 Debug Panel
			</h3>
			<button
				class="text-red-400 hover:text-red-300 text-xl"
				@click="$emit('close')"
			>
				✕
			</button>
		</div>

		<div class="space-y-3 text-sm">
			<!-- GA4 Status -->
			<div class="border-b border-neutral-700 pb-2">
				<div class="flex justify-between items-center">
					<strong>
						GA4 Status:
					</strong>
					<span :class="status.isInitialized ? 'text-green-400' : 'text-yellow-400'">
						{{ status.isInitialized ? 'Initialized' : 'Pending' }}
					</span>
				</div>
				<div class="text-xs text-neutral-400 mt-1">
					Queue: {{ status.queueLength }} events
					<span v-if="status.queueLength > 0">
						(oldest: {{ Math.round(status.oldestEventAge / 1000) }}s ago)
					</span>
				</div>
			</div>

			<!-- Visitor Info -->
			<div
				v-if="visitorData"
				class="border-b border-neutral-700 pb-2"
			>
				<div class="font-semibold mb-1 text-blue-400">
					Visitor Info
				</div>
				<div class="grid grid-cols-2 gap-x-4 text-xs">
					<div>
						Type: <span class="text-green-400">
							{{ isNewVisitor ? 'New' : 'Returning' }}
						</span>
					</div>
					<div>
						Visits: <span class="text-green-400">
							{{ visitorData.visitCount }}
						</span>
					</div>
					<div>
						Segment: <span class="text-green-400">
							{{ getVisitorSegment() }}
						</span>
					</div>
					<div>
						Days since last: <span class="text-green-400">
							{{ visitorData.daysSinceLastVisit }}
						</span>
					</div>
				</div>
			</div>

			<!-- Recent Events -->
			<div class="border-b border-neutral-700 pb-2 relative">
				<div class="font-semibold mb-1 text-purple-400 flex items-center justify-between gap-2 w-full">
					Recent Events
					<div class="flex gap-1">
						<motion.button
							:initial="{ scale: 1 }"
							:while-hover="{
								scale: 1.05,
								backgroundColor: sortNewestFirst ? '#10b981' : '#f59e0b',
								transition: { duration: 0.15 }
							}"
							:while-tap="{
								scale: 0.95,
								transition: { duration: 0.1 }
							}"
							:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
							:class="[
								'text-xs text-white rounded-sm inline-flex items-center justify-center px-1 pt-px pb-[3px] transition-colors shadow-sm hover:shadow-md',
								sortNewestFirst
									? 'bg-emerald-500 hover:bg-emerald-400'
									: 'bg-amber-500 hover:bg-amber-400'
							]"
							@click="toggleSort"
						>
							{{ sortNewestFirst ? '↓ Newest' : '↑ Oldest' }}
						</motion.button>
						<motion.button
							:initial="{ scale: 1 }"
							:while-hover="{
								scale: 1.05,
								backgroundColor: '#c084fc',
								transition: { duration: 0.15 }
							}"
							:while-tap="{
								scale: 0.95,
								transition: { duration: 0.1 }
							}"
							:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
							class="text-xs text-black bg-purple-400 rounded-sm inline-flex items-center justify-center px-1 pt-px pb-[3px] transition-colors hover:bg-purple-300/90 shadow-sm hover:shadow-md"
							@click="handleClearEvents"
						>
							Clear
						</motion.button>
					</div>
				</div>
				<motion.div
					v-if="recentEvents.length === 0"
					:initial="{ opacity: 0, y: 10 }"
					:animate="{ opacity: 1, y: 0 }"
					:transition="{ duration: 0.3, delay: 0.1 }"
					class="text-xs text-neutral-500"
				>
					No events tracked yet
				</motion.div>
				<div
					v-else
					ref="eventsContainer"
					class="space-y-2 max-h-64 overflow-y-auto overflow-x-clip relative"
				>
					<!-- Recent event cards with sophisticated slide animations -->
					<motion.div
						v-for="(event, index) in recentEvents"
						:key="`${event.timestamp}-${event.name}-${index}`"
						:initial="false"
						:animate="{
							opacity: 1,
							x: 0,
							y: 0,
							scale: 1,
							rotateY: 0
						}"
						:exit="{
							opacity: 0,
							x: -100,
							scale: 0.8,
							rotateY: 15,
							transition: { duration: 0.25, ease: 'easeInOut' }
						}"
						layout
						:transition="{
							type: 'spring',
							stiffness: 320,
							damping: 28,
							mass: 0.7,
							layout: {
								type: 'spring',
								stiffness: 450,
								damping: 22,
								duration: 0.35
							}
						}"
						:class="[
							{ 'mr-1': recentEvents.length > 1 },
							'transition-colors hover:bg-neutral-700 group',
							index % 2 === 0 ? 'bg-neutral-900' : 'bg-neutral-900/70'
						]"
						class="p-2 rounded text-xs shadow-sm hover:shadow-md"
						style="transform-style: preserve-3d;"
					>
						<div class="flex justify-between items-start">
							<span class="font-mono text-green-400">
								{{ event.name }}
							</span>
							<span class="text-neutral-400 group-hover:text-white/80">
								<NuxtTime
									:datetime="event.timestamp"
									relative
								/>
							</span>
						</div>
						<div
							v-if="event.params && Object.keys(event.params).length > 0"
							class="mt-1"
						>
							<div
								v-for="(value, key) in event.params"
								:key="key"
								class="text-neutral-400 truncate group-hover:text-neutral-300"
							>
								{{ key }}: <span class="text-white/90 group-hover:text-white">
									{{ formatParamValue(value) }}
								</span>
							</div>
						</div>
					</motion.div>
				</div>

				<!-- Scroll to Top Button -->
				<motion.button
					v-if="!isFirstEventVisible && recentEvents.length > 0"
					:initial="{ opacity: 0, scale: 0.8, y: 10 }"
					:animate="{ opacity: 1, scale: 1, y: 0 }"
					:exit="{ opacity: 0, scale: 0.8, y: 10 }"
					:while-hover="{ scale: 1.05 }"
					:while-tap="{ scale: 0.95 }"
					:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
					class="absolute top-8 right-2 w-8 h-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white text-xs hover:bg-white/20 transition-colors shadow-lg z-10"
					style="backdrop-filter: blur(8px);"
					@click="scrollToTop"
				>
					↑
				</motion.button>

				<!-- Scroll to Bottom Button -->
				<motion.button
					v-if="!isLastEventVisible && recentEvents.length > 0"
					:initial="{ opacity: 0, scale: 0.8, y: -10 }"
					:animate="{ opacity: 1, scale: 1, y: 0 }"
					:exit="{ opacity: 0, scale: 0.8, y: -10 }"
					:while-hover="{ scale: 1.05 }"
					:while-tap="{ scale: 0.95 }"
					:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
					class="absolute bottom-2 right-2 w-8 h-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white text-xs hover:bg-white/20 transition-colors shadow-lg z-10"
					style="backdrop-filter: blur(8px);"
					@click="scrollToBottom"
				>
					↓
				</motion.button>
			</div>

			<!-- Audience Properties -->
			<div class="border-b border-neutral-700 pb-2">
				<div class="font-semibold mb-1 text-orange-400">
					Audience Properties
				</div>
				<div class="bg-neutral-800 p-2 rounded">
					<pre class="text-xs text-neutral-300 whitespace-pre-wrap">
{{ audiencePropsFormatted }}
</pre>
				</div>
			</div>

			<!-- Page Info -->
			<div class="border-b border-neutral-700 pb-2">
				<div class="font-semibold mb-1 text-yellow-400">
					Page Info
				</div>
				<div class="text-xs space-y-1">
					<div>
						Path: <span class="text-white font-mono">
							{{ currentRoute.path }}
						</span>
					</div>
					<div>
						Name: <span class="text-white">
							{{ currentRoute.name || 'none' }}
						</span>
					</div>
					<div v-if="Object.keys(currentRoute.query).length">
						Query: <span class="text-white font-mono">
							{{ JSON.stringify(currentRoute.query) }}
						</span>
					</div>
					<div v-if="Object.keys(currentRoute.params).length">
						Params: <span class="text-white font-mono">
							{{ JSON.stringify(currentRoute.params) }}
						</span>
					</div>
				</div>
			</div>

			<!-- Actions -->
			<div class="space-y-2">
				<motion.button
					:initial="{ scale: 1 }"
					:while-hover="{ scale: 1.02 }"
					:while-tap="{ scale: 0.98 }"
					:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
					class="bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded text-xs w-full transition-colors"
					@click="testEvent"
				>
					Send Test Event
				</motion.button>

				<motion.button
					:initial="{ scale: 1 }"
					:while-hover="{ scale: 1.02 }"
					:while-tap="{ scale: 0.98 }"
					:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
					class="bg-purple-500 hover:bg-purple-600 px-3 py-1 rounded text-xs w-full transition-colors"
					@click="copyDebugInfo"
				>
					Copy Debug Info
				</motion.button>

				<motion.button
					:initial="{ scale: 1 }"
					:while-hover="{ scale: 1.02 }"
					:while-tap="{ scale: 0.98 }"
					:transition="{ type: 'spring', stiffness: 400, damping: 25 }"
					class="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-xs w-full transition-colors"
					@click="clearData"
				>
					Clear All Data & Reload
				</motion.button>
			</div>

			<div class="text-xs text-inverted/80 text-center">
				Press <kbd class="bg-toned/60 px-1 py-px rounded-xs inline-flex items-center justify-center">
					Ctrl
				</kbd> + <kbd class="bg-toned/60 px-1 py-px rounded-xs inline-flex items-center justify-center">
					Shift
				</kbd> + <kbd class="bg-toned/60 px-1 py-px w-4.5 rounded-xs inline-flex items-center justify-center">
					G
				</kbd>&nbsp;to toggle
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { motion } from 'motion-v'

defineEmits<{
	close: []
}>()

// Composables
const {
	getStatus, getEventHistory, clearEventHistory
} = useGA4EventQueue()
const { track } = useGA4Tracking()
const {
	visitorData,
	isNewVisitor,
	getVisitorSegment,
	getAudienceProperties,
	clearAllData
} = useVisitorTracking()
const currentRoute = useRoute()

// State
const status = computed(() => getStatus())
const allEvents = computed(() => getEventHistory())
const sortNewestFirst = ref(true) // Track sort order
const recentEvents = computed(() => {
	const events = [ ...allEvents.value ]
	return sortNewestFirst.value ? events.reverse() : events
})

// Ref for scrolling
const eventsContainer = ref<HTMLElement | null>(null)

// Reactive state for button visibility based on scroll position
const isFirstEventVisible = ref(true)
const isLastEventVisible = ref(true)

// Watch scroll position to show/hide buttons
const updateScrollButtonVisibility = () => {
	if (!eventsContainer.value) return

	const container = eventsContainer.value
	const scrollTop = container.scrollTop
	const scrollHeight = container.scrollHeight
	const clientHeight = container.clientHeight

	// Show scroll to top button when not at the top
	isFirstEventVisible.value = scrollTop <= 10

	// Show scroll to bottom button when not at the bottom
	isLastEventVisible.value = scrollTop >= scrollHeight - clientHeight - 10
}

// Add scroll listener when container is available
watch(eventsContainer, newContainer => {
	if (newContainer) {
		newContainer.addEventListener('scroll', updateScrollButtonVisibility)
		// Initial check
		updateScrollButtonVisibility()
	}
}, { immediate: true })

// Watch for new events (watch the original array length to avoid infinite loops)
watch(() => allEvents.value.length, (newLength, oldLength) => {
	// Only auto-scroll when a new event is added (not when events are cleared)
	if (newLength > (oldLength || 0) && sortNewestFirst.value) {
		// Multiple attempts with increasing delays to ensure scroll works
		const scrollToBottom = () => {
			if (eventsContainer.value) {
				const container = eventsContainer.value
				const maxScroll = container.scrollHeight - container.clientHeight
				container.scrollTo({
					top: maxScroll,
					behavior: 'smooth'
				})
			}
		}

		// Scroll after a short delay to ensure DOM updates
		setTimeout(scrollToBottom, 300)
	}
}, { flush: 'post' })

// Computed
const audiencePropsFormatted = computed(() => {
	const audienceProperties = getAudienceProperties()
	return JSON.stringify(audienceProperties, null, 2)
})

// Helper functions
const formatParamValue = (value: any) => {
	if (typeof value === 'object') return JSON.stringify(value)
	if (typeof value === 'string' && value.length > 50) {
		return value.substring(0, 50) + '...'
	}
	return value
}

// Actions
const testEvent = () => {
	track('debug_test_event', {
		test_id: Math.random().toString(36).substr(2, 9),
		test_value: Math.floor(Math.random() * 100),
		debug_source: 'panel'
	})
}

const copyDebugInfo = () => {
	const debugInfo = {
		status: status.value,
		visitor: visitorData.value,
		audienceProperties: getAudienceProperties(),
		recentEvents: recentEvents.value,
		route: {
			path: currentRoute.path,
			name: currentRoute.name,
			query: currentRoute.query,
			params: currentRoute.params
		},
		timestamp: new Date().toISOString()
	}

	navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2))
	alert('Debug info copied to clipboard!')
}

const handleClearEvents = () => {
	// wait for a 200ms delay to ensure any animations finish
	setTimeout(() => {
		clearEventHistory()
		if (eventsContainer.value) {
			eventsContainer.value.scrollTo({
				top: 0,
				behavior: 'smooth'
			})
		}
	}, 200)
}

const toggleSort = () => {
	sortNewestFirst.value = !sortNewestFirst.value
	// Delay scroll to allow DOM updates and layout animations to complete
	setTimeout(() => {
		if (eventsContainer.value) {
			const container = eventsContainer.value
			const scrollTop = sortNewestFirst.value
				? container.scrollHeight - container.clientHeight // Scroll to bottom for newest first
				: 0 // Scroll to top for oldest first
			container.scrollTo({
				top: scrollTop,
				behavior: 'smooth'
			})
		}
	}, 300)
}

const scrollToTop = () => {
	if (eventsContainer.value) {
		eventsContainer.value.scrollTo({
			top: 0,
			behavior: 'smooth'
		})
	}
}

const scrollToBottom = () => {
	if (eventsContainer.value) {
		const container = eventsContainer.value
		const maxScroll = container.scrollHeight - container.clientHeight
		container.scrollTo({
			top: maxScroll,
			behavior: 'smooth'
		})
	}
}

const clearData = () => {
	if (confirm('This will clear all visitor data and reload the page. Continue?')) {
		clearAllData()
		location.reload()
	}
}
</script>

<style scoped>
kbd {
	font-family: monospace;
	font-size: 0.9em;
}

/* custom scrollbar styles */
::-webkit-scrollbar {
	width: 6px;
	scrollbar-gutter: stable both-edges;
}
::-webkit-scrollbar-thumb {
	background-color: rgba(255, 255, 255, 0.5);
	border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
	background-color: rgba(255, 255, 255, 0.7);
}
::-webkit-scrollbar-track {
	background-color: transparent;
}
::-webkit-scrollbar-track:hover {
	background-color: rgba(255, 255, 255, 0.2);
}
::-webkit-scrollbar-corner {
	background-color: transparent;
}
::-webkit-scrollbar-button {
	display: none;
}
::-webkit-scrollbar-button:single-button {
	display: none;
}
::-webkit-scrollbar-button:double-button {
	display: none;
}
::-webkit-scrollbar-button:corner {
	display: none;
}
::-webkit-scrollbar-track-piece {
	background-color: transparent;
}
</style>
